import {useNavigate} from 'react-router';
import {ExamModule} from '@cp/model';
import useCreateQuizSession from './useCreateQuizSession';
import {castArray} from '@coolcolduk/util';
import {Maybe} from '@coolcolduk/typescript-util';
import {QuizTimedMode} from '@cp/model';

export interface CreateAndNavigateToQuizSessionOptions {
  difficulty?: number;
  count?: number;
  timedMode?: string;
  timeDurationSeconds?: number;
  navigationType?: 'practice' | 'session';
  sessionData?: unknown;
}

function processTopic(examModule: ExamModule, topic?: Maybe<string | string[]>): string[] {
  const validTopicNames = examModule.topics.map((t) => t.topic);
  if (!topic) {
    return validTopicNames;
  }
  const topicArray = castArray(topic);
  const filteredTopics = topicArray.filter((t: string) => validTopicNames.includes(t));
  return filteredTopics.length > 0 ? filteredTopics : validTopicNames;
}

export function useCreateAndNavigateToQuizSession() {
  const navigate = useNavigate();
  const {createQuizSession, loading, error} = useCreateQuizSession();

  const createAndNavigate = async (
    examModule: ExamModule,
    topic?: string | string[] | null,
    options: CreateAndNavigateToQuizSessionOptions = {},
  ) => {
    const {difficulty, count, timedMode, timeDurationSeconds, navigationType = 'practice', sessionData} = options;

    // Determine which topics to use
    const topicsToUse = processTopic(examModule, topic);

    // Use the module ID from examModule
    const moduleId = examModule.id;

    const resolvedTimedMode =
      timedMode === QuizTimedMode.TIMED || timedMode === QuizTimedMode.TIMED_PER_QUESTION
        ? timedMode
        : QuizTimedMode.PRACTICE;

    const session = await createQuizSession({
      moduleId,
      topics: topicsToUse,
      difficulty: difficulty ?? 1,
      count: count ?? 10,
      timedMode: resolvedTimedMode,
      timeDurationSeconds,
    });

    if (session) {
      if (navigationType === 'practice') {
        // Navigate to practice session route
        navigate(`/course/${moduleId}/practice/${session.id}`);
      } else {
        // Navigate to regular session route with state
        navigate(`/session/${session.id}`, {state: {sessionData: sessionData || session}});
      }
    }

    return session;
  };

  return {
    createAndNavigate,
    loading,
    error,
  };
}
